import React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Search } from "lucide-react";

const CATEGORIES = [
  { key: "users", label: "Users", path: (r) => `/admin/customers/${r.id}`, render: (r) => `${r.email || r.name} · ${(r.credits||0).toLocaleString()} credits` },
  { key: "orders", label: "Orders", path: (r) => `/admin/creem?order=${r.id}`, render: (r) => `${r.invoice_number || r.id} · $${((r.total_cents||0)/100).toFixed(2)} · ${r.status}` },
  { key: "refunds", label: "Refunds", path: (r) => `/admin/cases`, render: (r) => `${r.creem_refund_id} · $${((r.amount_cents||0)/100).toFixed(2)} · ${r.status}` },
  { key: "disputes", label: "Disputes", path: () => `/admin/cases`, render: (r) => `${r.creem_dispute_id} · ${r.normalized_status}` },
  { key: "webhooks", label: "Webhooks", path: () => `/admin/webhooks`, render: (r) => `${r.event_id} · ${r.event_type} · ${r.processed ? "ok" : "failed"}` },
  { key: "subscriptions", label: "Subscriptions", path: (r) => `/admin/customers/${r.user_id}`, render: (r) => `${r.id} · ${r.status}` },
  { key: "api_keys", label: "API keys", path: (r) => `/admin/customers/${r.user_id}`, render: (r) => `${r.key_prefix}… · ${r.revoked_at ? "revoked" : "active"}` },
];

export default function CommandPalette() {
  const { t } = useLang();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [sel, setSel] = React.useState(0);
  const inputRef = React.useRef(null);
  const nav = useNavigate();

  const flat = React.useMemo(() => {
    if (!results) return [];
    const out = [];
    CATEGORIES.forEach((c) => {
      (results[c.key] || []).forEach((r) => out.push({ cat: c, row: r }));
    });
    return out;
  }, [results]);

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (open && e.key === "Escape") {
        setOpen(false);
      } else if (open && e.key === "ArrowDown") {
        setSel((s) => Math.min(s + 1, flat.length - 1));
      } else if (open && e.key === "ArrowUp") {
        setSel((s) => Math.max(s - 1, 0));
      } else if (open && e.key === "Enter" && flat[sel]) {
        const { cat, row } = flat[sel];
        setOpen(false);
        nav(cat.path(row));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, flat, sel, nav]);

  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  React.useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    setBusy(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.get("/api/admin/search", { params: { q } });
        setResults(r.data);
        setSel(0);
      } finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-20"
         onClick={() => setOpen(false)} data-testid="command-palette">
      <div className="lp-card max-w-xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/8">
          <Search className="w-4 h-4 text-white/50" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={t("admin.cmdk.placeholder")}
                 className="flex-1 bg-transparent outline-none text-sm text-white/95"
                 data-testid="cmdk-input" />
          {busy && <div className="text-white/40 text-xs">…</div>}
          <kbd className="text-[10px] text-white/40">ESC</kbd>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {!results && (
            <div className="p-6 text-center text-white/40 text-sm">
              {t("admin.cmdk.hint")}
            </div>
          )}
          {results && flat.length === 0 && (
            <div className="p-6 text-center text-white/40 text-sm">{t("admin.cmdk.no_results")}</div>
          )}
          {CATEGORIES.map((cat) => {
            const rows = results?.[cat.key] || [];
            if (rows.length === 0) return null;
            return (
              <div key={cat.key} className="px-2 py-1">
                <div className="text-[10px] uppercase tracking-widest text-white/40 px-2 py-1">{cat.label}</div>
                {rows.map((r, i) => {
                  const idx = flat.findIndex((x) => x.cat.key === cat.key && x.row === r);
                  const active = idx === sel;
                  return (
                    <button
                      key={r.id || r.event_id || i}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm ${active ? "bg-amber-500/20 text-amber-100" : "text-white/85 hover:bg-white/5"}`}
                      onClick={() => { setOpen(false); nav(cat.path(r)); }}
                      data-testid={`cmdk-${cat.key}-${i}`}
                    >{cat.render(r)}</button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
